// Local macroeconomic data API service
import { API_BASE_URL } from '../config/api.config';
import { getRuntimeConfig } from '../utils/runtime-config';
export interface MacroeconomicData {
  countryId: string;
  countryName: string;
  year: number;
  gdpPerCapita?: number;
  unemploymentRate?: number;
  inflationRate?: number;
  laborForceParticipation?: number;
  grossNationalIncome?: number;
  populationTotal?: number;
  urbanPopulation?: number;
  internetUsers?: number;
  electricityAccess?: number;
  educationExpenditure?: number;
  healthExpenditure?: number;
  dataSource?: string;
  lastUpdated?: string;
}

class MacroeconomicApiService {
  private baseUrl: string;
  
  constructor() {
    this.baseUrl = API_BASE_URL;
  }
  
  async getAllMacroeconomicData(year?: number): Promise<MacroeconomicData[]> {
    // Skip API call if using mock data
    if (getRuntimeConfig('REACT_APP_USE_MOCK_DATA') === 'true') {
      return this.getMockAllMacroeconomicData(year || 2022);
    }
    
    try {
      const params = new URLSearchParams();
      if (year) {
        params.append('year', year.toString());
      }
      
      const url = `${this.baseUrl}/api/macroeconomic${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch macroeconomic data: ${response.statusText}`);
      }
      
      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.warn('API unavailable, using mock macroeconomic data');
      // Return mock data when API is unavailable
      return this.getMockAllMacroeconomicData(year || 2022);
    }
  }
  
  private getMockAllMacroeconomicData(year: number): MacroeconomicData[] {
    // Mock data for major countries
    return [
      {
        countryId: 'USA',
        countryName: 'United States',
        year,
        gdpPerCapita: 77885,
        unemploymentRate: 3.6,
        inflationRate: 8.0,
        laborForceParticipation: 63.4,
        grossNationalIncome: 24000000000000,
        populationTotal: 333287557,
        urbanPopulation: 83.08,
        internetUsers: 92.15,
        electricityAccess: 100,
        educationExpenditure: 5.0,
        healthExpenditure: 17.8,
        dataSource: 'Mock Data',
        lastUpdated: new Date().toISOString()
      },
      {
        countryId: 'DEU',
        countryName: 'Germany',
        year,
        gdpPerCapita: 51203,
        unemploymentRate: 3.0,
        inflationRate: 7.9,
        laborForceParticipation: 61.5,
        grossNationalIncome: 4300000000000,
        populationTotal: 83240525,
        urbanPopulation: 77.54,
        internetUsers: 91.98,
        electricityAccess: 100,
        educationExpenditure: 4.5,
        healthExpenditure: 11.7,
        dataSource: 'Mock Data',
        lastUpdated: new Date().toISOString()
      },
      {
        countryId: 'GBR',
        countryName: 'United Kingdom',
        year,
        gdpPerCapita: 47334,
        unemploymentRate: 3.7,
        inflationRate: 9.1,
        laborForceParticipation: 60.5,
        grossNationalIncome: 3200000000000,
        populationTotal: 67886004,
        urbanPopulation: 84.15,
        internetUsers: 94.78,
        electricityAccess: 100,
        educationExpenditure: 5.5,
        healthExpenditure: 10.2,
        dataSource: 'Mock Data',
        lastUpdated: new Date().toISOString()
      },
      {
        countryId: 'FRA',
        countryName: 'France',
        year,
        gdpPerCapita: 44995,
        unemploymentRate: 7.3,
        inflationRate: 5.9,
        laborForceParticipation: 56.2,
        grossNationalIncome: 3000000000000,
        populationTotal: 67391582,
        urbanPopulation: 81.24,
        internetUsers: 85.62,
        electricityAccess: 100,
        educationExpenditure: 5.4,
        healthExpenditure: 11.2,
        dataSource: 'Mock Data',
        lastUpdated: new Date().toISOString()
      }
    ];
  }
  
  async getCountryMacroeconomicData(countryId: string, year: number = 2022): Promise<MacroeconomicData | null> {
    try {
      const url = `${this.baseUrl}/api/macroeconomic/${countryId}?year=${year}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        if (response.status === 404) {
          // Return mock data for demo purposes when API is not available
          return this.getMockMacroeconomicData(countryId, year);
        }
        throw new Error(`Failed to fetch country data: ${response.statusText}`);
      }
      
      const result = await response.json();
      return result.data || null;
    } catch (error) {
      console.warn(`API unavailable for ${countryId}, using mock data`);
      // Return mock data as fallback
      return this.getMockMacroeconomicData(countryId, year);
    }
  }

  private getMockMacroeconomicData(countryId: string, year: number): MacroeconomicData {
    // Mock data for demo purposes
    const mockData: { [key: string]: Partial<MacroeconomicData> } = {
      usa: {
        gdpPerCapita: 76330,
        unemploymentRate: 3.7,
        inflationRate: 4.7,
        urbanPopulation: 83.1,
        internetUsers: 92.0
      },
      uk: {
        gdpPerCapita: 48420,
        unemploymentRate: 3.7,
        inflationRate: 9.1,
        urbanPopulation: 84.4,
        internetUsers: 96.5
      },
      germany: {
        gdpPerCapita: 48720,
        unemploymentRate: 3.0,
        inflationRate: 8.7,
        urbanPopulation: 77.6,
        internetUsers: 91.0
      },
      france: {
        gdpPerCapita: 43660,
        unemploymentRate: 7.3,
        inflationRate: 5.9,
        urbanPopulation: 81.2,
        internetUsers: 85.6
      },
      canada: {
        gdpPerCapita: 52050,
        unemploymentRate: 5.0,
        inflationRate: 6.8,
        urbanPopulation: 81.7,
        internetUsers: 93.0
      },
      poland: {
        gdpPerCapita: 18000,
        unemploymentRate: 2.9,
        inflationRate: 14.4,
        urbanPopulation: 60.0,
        internetUsers: 87.0
      },
      'czech-republic': {
        gdpPerCapita: 27600,
        unemploymentRate: 2.2,
        inflationRate: 15.1,
        urbanPopulation: 74.2,
        internetUsers: 88.0
      },
      austria: {
        gdpPerCapita: 53260,
        unemploymentRate: 4.8,
        inflationRate: 8.6,
        urbanPopulation: 59.1,
        internetUsers: 88.6
      },
      slovakia: {
        gdpPerCapita: 21390,
        unemploymentRate: 6.1,
        inflationRate: 12.1,
        urbanPopulation: 53.8,
        internetUsers: 89.0
      }
    };

    const data = mockData[countryId] || {
      gdpPerCapita: 35000,
      unemploymentRate: 5.0,
      inflationRate: 3.0,
      urbanPopulation: 70.0,
      internetUsers: 85.0
    };

    return {
      countryId,
      countryName: countryId.charAt(0).toUpperCase() + countryId.slice(1).replace('-', ' '),
      year,
      ...data
    } as MacroeconomicData;
  }
  
  // Convert to map format for easy lookup
  async getMacroeconomicDataMap(year?: number): Promise<{ [countryId: string]: MacroeconomicData }> {
    const data = await this.getAllMacroeconomicData(year);
    const map: { [countryId: string]: MacroeconomicData } = {};
    
    data.forEach(item => {
      map[item.countryId] = item;
    });
    
    return map;
  }
  
  // For admin use - update macroeconomic data
  async updateMacroeconomicData(data: Partial<MacroeconomicData>): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/macroeconomic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update data: ${response.statusText}`);
      }
      
      return true;
    } catch (error) {
      console.error('Error updating macroeconomic data:', error);
      return false;
    }
  }
}

export const macroeconomicApi = new MacroeconomicApiService();
export default macroeconomicApi;